import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Alert from '@mui/material/Alert'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'

import { selectOrganisation } from '@/app/slices/organisationSlice'
import { getSupportedLocale } from '@/common/utils'
import { QUOTE_STATUS_ARRAY } from '@/common/utils/Constants/Constants'

const STATUSES_WITH_EXPIRY_DATE = [
    QUOTE_STATUS_ARRAY.NotCalculated,
    QUOTE_STATUS_ARRAY.Calculated,
    QUOTE_STATUS_ARRAY.Draft,
    QUOTE_STATUS_ARRAY.Issued,
]

/**
 * Warns that a material on the quote expires before the quote expiry date the user set by hand.
 * Renders nothing unless the backend flags it and the quote still shows an expiry date.
 * @param {object} props
 * @param {object} [props.quote] - The quote from `getQuote`.
 * @returns {JSX.Element|null}
 */
const QuoteMaterialExpiryWarning = ({ quote }) => {
    const { t } = useTranslation()
    const organisation = useSelector(selectOrganisation)

    if (
        !quote?.hasSheetExpiringBeforeExpiryDate ||
        !quote?.earliestSheetExpiryDate ||
        !STATUSES_WITH_EXPIRY_DATE.includes(quote?.status)
    ) {
        return null
    }

    const date = dayjs(quote.earliestSheetExpiryDate)
        .locale(getSupportedLocale(organisation?.locale))
        .format('DD-MMM-YYYY')

    return (
        <Alert
            severity="warning"
            sx={{
                border: (theme) => `1px solid ${theme.palette.warning.light}`,
                borderRadius: 2,
                marginBottom: 3,
                width: '100%',
            }}
        >
            {t('A material on this quote expires on {{date}}, before the quote expiry date.', { date })}
        </Alert>
    )
}

QuoteMaterialExpiryWarning.propTypes = {
    quote: PropTypes.object,
}

export default QuoteMaterialExpiryWarning
